import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { fetchCRONExpressions } from '@home/services/common';
import { fetchUserSearchTrendingStatus } from '@home/services/search';
import type {
  IUserCenterEvent,
  IUserCenterEventOptionState,
  IUseUserCenterEventsResult,
} from '@home/types/userCenterEvents';

const OPTION_KEY = 'user_center_events';

/**
 * Custom hook for managing user center event-driven badge visibility
 * Replaces cron-based notification logic with event-driven approach
 *
 * @returns {IUseUserCenterEventsResult} Hook state and methods
 */
export default function useUserCenterEvents(): IUseUserCenterEventsResult {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [events, setEvents] = useState<IUserCenterEvent[]>([]);
  const [triggeredState, setTriggeredState] = useState<IUserCenterEventOptionState>({
    triggered_ids: [],
  });

  /**
   * Fetch events from cond API
   */
  const fetchEvents = useCallback(async (): Promise<IUserCenterEvent[]> => {
    try {
      const response = await fetchCRONExpressions({ prefix: 'user_center_*' });

      if (response.code === 0 && response.data?.cron_expressions) {
        const rawEvents = response.data.cron_expressions;

        const mappedEvents = rawEvents
          .map((rule: any) => {
            let parsedExt: any = {};

            if (rule.ext) {
              try {
                parsedExt = typeof rule.ext === 'string' ? JSON.parse(rule.ext) : rule.ext;
              } catch (parseErr) {
                console.warn('[useUserCenterEvents] Failed to parse ext:', parseErr);
              }
            }

            return {
              id: String(rule.id),
              create_time: rule.create_time,
              tag: rule.tag,
              path: parsedExt.path,
              expiry: parsedExt.expiry,
              crontab: rule.crontab,
            } as IUserCenterEvent;
          })
          .filter((event: IUserCenterEvent) => {
            const hasPath = !!event.path;
            const hasExpiry = !!event.expiry;

            if (!hasPath || !hasExpiry) {
              return false;
            }

            return true;
          });

        return mappedEvents;
      }

      return [];
    } catch (err: any) {
      console.error('[useUserCenterEvents] Failed to fetch user center events:', err);
      throw err;
    }
  }, []);

  /**
   * Fetch triggered state from user option API
   */
  const fetchTriggeredState = useCallback(async (): Promise<IUserCenterEventOptionState> => {
    try {
      const response = await fetchUserSearchTrendingStatus({
        action: 'get',
        keys: [OPTION_KEY],
      });

      const stateStr = response?.[OPTION_KEY] || '';

      if (stateStr) {
        try {
          const parsedState = JSON.parse(stateStr);
          return parsedState;
        } catch (parseErr) {
          console.warn('[useUserCenterEvents] Failed to parse triggered state:', parseErr);
        }
      }

      return { triggered_ids: [] };
    } catch (err: any) {
      console.error('[useUserCenterEvents] Failed to fetch triggered state:', err);
      return { triggered_ids: [] };
    }
  }, []);

  /**
   * Filter and sort active events
   * Active = not expired AND id not in triggered_ids
   * Sort by create_time asc (oldest first), then by id
   */
  const getActiveEvents = useCallback(
    (allEvents: IUserCenterEvent[], state: IUserCenterEventOptionState): IUserCenterEvent[] => {
      const now = moment();

      const activeEvents = allEvents.filter((event) => {
        if (!event.path) {
          return false;
        }

        const expiryDate = moment(event.expiry, 'YYYY-MM-DD HH:mm:ss');
        if (!expiryDate.isValid()) {
          return false;
        }

        if (now.isAfter(expiryDate)) {
          return false;
        }

        if (state.triggered_ids.includes(event.id)) {
          return false;
        }

        return true;
      });

      const fmt = 'YYYY-MM-DD HH:mm:ss';
      return activeEvents.sort((a, b) => {
        const aTime = moment(a.create_time, fmt);
        const bTime = moment(b.create_time, fmt);

        const aValue = aTime.isValid() ? aTime.valueOf() : 0;
        const bValue = bTime.isValid() ? bTime.valueOf() : 0;

        const timeCompare = aValue - bValue;
        if (timeCompare !== 0) {
          return timeCompare;
        }
        return a.id.localeCompare(b.id);
      });
    },
    [],
  );

  /**
   * Prune expired event ids from triggered_ids
   */
  const pruneExpiredTriggeredIds = useCallback(
    (allEvents: IUserCenterEvent[], state: IUserCenterEventOptionState): string[] => {
      const now = moment();
      const validEventIds = new Set(
        allEvents
          .filter((event) => {
            const expiryDate = moment(event.expiry, 'YYYY-MM-DD HH:mm:ss');
            return expiryDate.isValid() && now.isBefore(expiryDate);
          })
          .map((event) => event.id),
      );

      return state.triggered_ids.filter((id) => validEventIds.has(id));
    },
    [],
  );

  /**
   * Trigger one or more events in a single API call
   */
  const triggerEvent = useCallback(
    async (eventIdOrIds: string | string[]): Promise<void> => {
      const eventIds = Array.isArray(eventIdOrIds) ? eventIdOrIds : [eventIdOrIds];
      if (!eventIds.length) return;

      try {
        const prunedIds = pruneExpiredTriggeredIds(events, triggeredState);
        const now = moment().format('YYYY-MM-DD HH:mm:ss');
        const updatedIds = Array.from(new Set([...prunedIds, ...eventIds]));
        const updatedAt = { ...(triggeredState.triggered_at || {}) };
        eventIds.forEach((id) => {
          updatedAt[id] = now;
        });

        const newState: IUserCenterEventOptionState = {
          triggered_ids: updatedIds,
          triggered_at: updatedAt,
        };

        await fetchUserSearchTrendingStatus({
          action: 'set',
          key: OPTION_KEY,
          value: JSON.stringify(newState),
        });

        setTriggeredState(newState);
      } catch (err: any) {
        console.error(`[useUserCenterEvents] Failed to trigger event(s) ${eventIdOrIds}:`, err);
        throw err;
      }
    },
    [events, triggeredState, pruneExpiredTriggeredIds],
  );

  /**
   * Refresh events and triggered state
   */
  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const [fetchedEvents, fetchedState] = await Promise.all([
        fetchEvents(),
        fetchTriggeredState(),
      ]);

      setEvents(fetchedEvents);
      setTriggeredState(fetchedState);
    } catch (err: any) {
      setEvents([]);
      setTriggeredState({ triggered_ids: [] });
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchEvents, fetchTriggeredState]);

  /**
   * Initial load
   */
  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeEvents = getActiveEvents(events, triggeredState);

  return {
    loading,
    error,
    hasActiveEvents: activeEvents.length > 0,
    activeEvents,
    firstActiveEvent: activeEvents[0],
    triggerEvent,
    refresh,
  };
}
